import type { Tone } from "../styles";

/**
 * One map from a domain status to a colour, for the whole product.
 *
 * The design system page calls this "Status chip — one source of truth", and
 * the phrase is the requirement rather than a description. Seventeen order
 * statuses appear on the orders table, the fulfilment board, the store portal,
 * the driver app and three printed documents; the moment two screens decide
 * separately what colour `on_hold` is, somebody learns that amber means one
 * thing here and another there, and the colour stops carrying information.
 *
 * The vocabulary, from the design page:
 *
 * | tone | states |
 * |---|---|
 * | `neutral` | draft, pending |
 * | `info` | allocated, in progress, picking, packed |
 * | `brand` | in transit, out for delivery |
 * | `success` | delivered, done, paid, restocked, active |
 * | `warning` | on hold, awaiting, unpriced |
 * | `danger` | failed, returned, cancelled |
 *
 * A `warning` tone with an alert icon is reserved for conflict and variance —
 * the cases where the number on the screen and the number in the warehouse
 * disagree, which is not the same as "waiting" and should not look like it.
 *
 * **Colour is never the only signal.** Every chip carries its label, so the map
 * decides emphasis rather than meaning. That is what makes it safe for somebody
 * who cannot tell amber from green.
 */

/** How a status should be drawn. */
export interface StatusTone {
	tone: Tone;
	/** True for conflict and variance: the chip grows an alert triangle. */
	alert?: boolean;
}

/** A domain's statuses, mapped to tones. */
export type ToneMap = Readonly<Record<string, StatusTone>>;

/**
 * The tones that are the same wherever they appear.
 *
 * Checked after a domain's own map, so a domain can override one — `returned`
 * is a failure for an order and an ordinary step for a return — without
 * restating the twenty that it does not.
 */
const SHARED: ToneMap = {
	draft: { tone: "neutral" },
	pending: { tone: "neutral" },
	archived: { tone: "neutral" },
	inactive: { tone: "neutral" },

	allocated: { tone: "info" },
	in_progress: { tone: "info" },
	picking: { tone: "info" },
	packed: { tone: "info" },
	processing: { tone: "info" },

	in_transit: { tone: "brand" },
	out_for_delivery: { tone: "brand" },

	delivered: { tone: "success" },
	done: { tone: "success" },
	completed: { tone: "success" },
	paid: { tone: "success" },
	restocked: { tone: "success" },
	active: { tone: "success" },
	approved: { tone: "success" },

	on_hold: { tone: "warning" },
	awaiting: { tone: "warning" },
	unpriced: { tone: "warning" },
	suspended: { tone: "warning" },

	failed: { tone: "danger" },
	returned: { tone: "danger" },
	cancelled: { tone: "danger" },
	rejected: { tone: "danger" },
	closed: { tone: "danger" },

	// The two that carry an icon as well as a colour. A variance is not a state
	// something is waiting in — it is a disagreement between the system and the
	// warehouse, and it needs to be distinguishable from "on hold" at a glance.
	conflict: { tone: "warning", alert: true },
	variance: { tone: "warning", alert: true },
};

/**
 * Per-domain overrides and additions.
 *
 * `product` is registered here because EP00-S17 renders the products table on
 * the primitives this story delivers. Every later epic registers its own from
 * its module folder — `registerStatusTones("order", …)` in `modules/orders` —
 * so the statuses live beside the code that produces them and this file does
 * not have to grow a section per epic.
 */
const domains = new Map<string, ToneMap>([
	[
		"product",
		{
			active: { tone: "success" },
			archived: { tone: "neutral" },
		},
	],
]);

/**
 * Registers a domain's statuses.
 *
 * Called once at module load from a module's `index.ts`. Re-registering the
 * same domain replaces it, which is what a hot reload does; it is not something
 * two modules should both be doing, and if they are, the second wins silently —
 * a collision worth noticing, which is why the domain is a module id.
 */
export function registerStatusTones(domain: string, map: ToneMap): void {
	domains.set(domain, map);
}

/** The domains registered so far. For the demo page and for tests. */
export function registeredDomains(): string[] {
	return [...domains.keys()].sort();
}

/** Keys already warned about, so a column of 200 unknown rows logs once. */
const warned = new Set<string>();

/** Clears the warned memo. For tests, which need each case isolated. */
export function resetToneWarnings(): void {
	warned.clear();
}

/**
 * The tone for a status.
 *
 * An unknown status is `neutral` plus a development warning, never a throw.
 * Statuses arrive from the server, and a build that has not caught up with a
 * new one must render a grey chip with the right label rather than take the
 * table down — the label still carries the meaning, which is the point of never
 * relying on colour alone.
 *
 * The warning is the other half: grey is a legitimate tone, so without it a
 * status that quietly fell out of the map would look deliberate forever.
 */
export function toneFor(domain: string, status: string): StatusTone {
	const key = normalise(status);
	const found = domains.get(domain)?.[key] ?? SHARED[key];

	if (found !== undefined) {
		return found;
	}

	if (import.meta.env.DEV) {
		const memo = `${domain}.${key}`;
		if (!warned.has(memo)) {
			warned.add(memo);
			console.warn(
				JSON.stringify({
					event: "ui.unknown_status",
					domain,
					status: key,
					hint: "Add it to the domain's map via registerStatusTones, or to SHARED if every domain means the same thing by it.",
				}),
			);
		}
	}
	return { tone: "neutral" };
}

/**
 * Normalises a status to the map's spelling.
 *
 * The API sends `out_for_delivery`; a hand-written call site might say
 * `"Out for delivery"` or `"out-for-delivery"`. All three mean one status, and
 * a map that only answered to the first would push the other two into the
 * unknown path — where they would render grey and look intentional.
 */
function normalise(status: string): string {
	return status
		.trim()
		.toLowerCase()
		.replaceAll(/[\s-]+/g, "_");
}
